import React from 'react';
import { Calendar, Clock, CalendarDays, CalendarRange, X, Zap } from 'lucide-react';
import { intervalWeight } from '../../../utils/habitProgress';
import { missingDaysMessage } from '../../../utils/habitSchedule';
import type { HabitInterval, HabitSchedule } from '../../../types';

interface IntervalSelectProps {
  value: HabitSchedule;
  onChange: (schedule: HabitSchedule) => void;
  color?: string;
  /** Times per day — used only to spell out the schedule summary. */
  targetPerDay?: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Leap year so the picker can offer Feb 29.
const PICKER_YEAR = 2024;
const INTERVALS: Array<{ id: HabitInterval; label: string; icon: typeof Clock }> = [
  { id: 'daily', label: 'Daily', icon: Clock },
  { id: 'weekly', label: 'Weekly', icon: Calendar },
  { id: 'monthly', label: 'Monthly', icon: CalendarDays },
  { id: 'yearly', label: 'Yearly', icon: CalendarRange },
];

const pad2 = (n: number) => String(n).padStart(2, '0');
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export function IntervalSelect({ value, onChange, color, targetPerDay = 1 }: IntervalSelectProps) {
  const [yearMonth, setYearMonth] = React.useState(() => new Date().getMonth());

  const handleIntervalChange = (e: React.MouseEvent, interval: HabitInterval) => {
    e.preventDefault();
    const newSchedule: HabitSchedule = {
      interval,
      days: interval === 'weekly' ? [0, 1, 2, 3, 4, 5, 6] : undefined,
      monthDays: interval === 'monthly' ? [1] : undefined,
      yearDates: interval === 'yearly' ? [`${PICKER_YEAR}-${pad2(new Date().getMonth() + 1)}-${pad2(new Date().getDate())}`] : undefined,
    };
    onChange(newSchedule);
  };

  const toggleWeekday = (e: React.MouseEvent, day: number) => {
    e.preventDefault();
    if (!value.days) return;
    const newDays = value.days.includes(day) ? value.days.filter((d) => d !== day) : [...value.days, day].sort();
    onChange({ ...value, days: newDays });
  };

  const toggleMonthDay = (e: React.MouseEvent, day: number) => {
    e.preventDefault();
    if (!value.monthDays) return;
    const newDays = value.monthDays.includes(day)
      ? value.monthDays.filter((d) => d !== day)
      : [...value.monthDays, day].sort((a, b) => a - b);
    onChange({ ...value, monthDays: newDays });
  };

  const toggleYearDate = (e: React.MouseEvent, monthIdx: number, day: number) => {
    e.preventDefault();
    const key = `${PICKER_YEAR}-${pad2(monthIdx + 1)}-${pad2(day)}`;
    const current = value.yearDates ?? [];
    const next = current.includes(key) ? current.filter((d) => d !== key) : [...current, key].sort();
    onChange({ ...value, yearDates: next });
  };

  // "2 days/week · 3× each" style recap so the schedule reads in plain language.
  const perDay = targetPerDay > 1 ? ` · ${targetPerDay}× each` : '';
  const summary = (() => {
    switch (value.interval) {
      case 'daily':
        return targetPerDay > 1 ? `Every day · ${targetPerDay}× per day` : 'Every day';
      case 'weekly': {
        const n = value.days?.length ?? 0;
        return `${n} day${n === 1 ? '' : 's'}/week${perDay}`;
      }
      case 'monthly': {
        const n = value.monthDays?.length ?? 0;
        return `${n} day${n === 1 ? '' : 's'}/month${perDay}`;
      }
      case 'yearly': {
        const n = value.yearDates?.length ?? 0;
        return `${n} date${n === 1 ? '' : 's'}/year${perDay}`;
      }
    }
  })();

  // ⚠️ No day picked → the habit could never be due (utils/habitSchedule.ts). Said in
  // place of the "0 days/week" recap, which read like a valid schedule.
  const missing = missingDaysMessage(value);

  const daysInPickerMonth = new Date(PICKER_YEAR, yearMonth + 1, 0).getDate();
  const yearDates = value.yearDates ?? [];

  return (
    <div className="space-y-4">
      {/* Interval Type Selection */}
      <div className="flex flex-wrap gap-2">
        {INTERVALS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={(e) => handleIntervalChange(e, id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              value.interval === id ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
            style={value.interval === id ? { color, backgroundColor: `${color}15` } : undefined}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Weekly Selector */}
      {value.interval === 'weekly' && (
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((day, index) => (
            <button
              key={day}
              type="button"
              onClick={(e) => toggleWeekday(e, index)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                value.days?.includes(index) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
              style={value.days?.includes(index) ? { color, backgroundColor: `${color}15` } : undefined}
            >
              {day}
            </button>
          ))}
        </div>
      )}

      {/* Monthly Selector */}
      {value.interval === 'monthly' && (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
            <button
              key={day}
              type="button"
              onClick={(e) => toggleMonthDay(e, day)}
              className={`p-2 rounded-md text-sm transition-colors ${
                value.monthDays?.includes(day) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
              style={value.monthDays?.includes(day) ? { color, backgroundColor: `${color}15` } : undefined}
            >
              {day}
            </button>
          ))}
        </div>
      )}

      {/* Yearly Selector — pick specific dates from any month */}
      {value.interval === 'yearly' && (
        <div className="space-y-3">
          {/* Month tabs */}
          <div className="flex flex-wrap gap-1">
            {MONTHS.map((m, i) => {
              const hasDates = yearDates.some((d) => d.slice(5, 7) === pad2(i + 1));
              return (
                <button
                  key={m}
                  type="button"
                  onClick={(e) => { e.preventDefault(); setYearMonth(i); }}
                  className={`rounded-md px-2 py-1 text-xs transition-colors ${
                    yearMonth === i ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  style={yearMonth === i ? { color, backgroundColor: `${color}15` } : undefined}
                >
                  {m}
                  {hasDates && <span className="ml-0.5 inline-block h-1 w-1 rounded-full align-middle" style={{ backgroundColor: color }} />}
                </button>
              );
            })}
          </div>
          {/* Day grid for the selected month */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: daysInPickerMonth }, (_, i) => i + 1).map((day) => {
              const key = `${PICKER_YEAR}-${pad2(yearMonth + 1)}-${pad2(day)}`;
              const selected = yearDates.includes(key);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={(e) => toggleYearDate(e, yearMonth, day)}
                  className={`p-2 rounded-md text-sm transition-colors ${
                    selected ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  style={selected ? { color, backgroundColor: `${color}15` } : undefined}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {/* Selected dates as removable chips */}
          {yearDates.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {yearDates.map((d) => {
                const [, mm, dd] = d.split('-').map(Number);
                return (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" style={{ color, backgroundColor: `${color}15` }}>
                    {MONTHS[mm - 1]} {ordinal(dd)}
                    <button
                      type="button"
                      onClick={(e) => toggleYearDate(e, mm - 1, dd)}
                      aria-label={`Remove ${MONTHS[mm - 1]} ${dd}`}
                      className="hover:opacity-70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Plain-language recap of the schedule + the interval's XP weight */}
      <div className="flex flex-wrap items-center gap-2">
        {missing ? (
          <p role="alert" className="text-xs font-medium text-red-400">{missing}</p>
        ) : (
          <p className="text-xs text-muted-foreground">{summary}</p>
        )}
        {!missing && intervalWeight(value.interval) > 1 && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ color, backgroundColor: `${color}15` }}
            title={`${value.interval} habits earn ${intervalWeight(value.interval)}× the base XP per completion`}
          >
            <Zap className="h-3 w-3" />
            ×{intervalWeight(value.interval)} XP
          </span>
        )}
      </div>
    </div>
  );
}
